// PoScanLineCard — one line off the scanned purchase order, in one of its two
// states.
//
//   MATCHED  the description hit an item that already exists. Show which one,
//            and let it be re-pointed, because an exact SKU match on a vendor's
//            wording is right most of the time and not all of the time.
//   MINT     nobody has cataloged this yet. The card asks for what a catalog
//            row of THAT KIND cannot be born without, and asks for it HERE
//            rather than sending the person to another screen with the box
//            still open.
//
// ── KIND FIRST, THE SAME QUESTION THE WIZARD ASKS ───────────────────────────
// The card used to open with a SKU box holding a string the grammar guessed,
// which asked a person to compose a code for a grommet. Short codes exist so
// nobody has to: the card asks the ONE question whose answer reshapes the rest
// (`Parts/KindPicker`, lifted out of the wizard's step one), and then asks only
// what that kind actually has.
//
//   SHORT KIND   a name, required, defaulted from the line's own description.
//                No SKU control anywhere: the server assigns GMT-7K3Q at
//                confirm, inside the same transaction as the purchase order.
//   BOARD KIND   the SKU builder with its Board segment locked to the chosen
//                kind, exactly as step two of the wizard renders it. The
//                composed string is derived from the segments, never typed.
//   NO KIND      the card says so and Confirm names the line. The reader's
//                guess is a guess; a wrong kind is a wrong code forever.
//
// The bulk flow is untouched: thirty lines arrive answered from the read and
// commit on ONE confirm. Only the shape of the per-line question changed.
//
// ── BOTH AXES, NEITHER DEFAULTED ────────────────────────────────────────────
// material_type and commodity_class are NOT NULL with no database default since
// migration 016, for the reason the whole taxonomy exists: a default is a
// classification nobody made, which is how 33 catalog rows ended up filed as
// 'component'. So:
//
//   material_type    PRESELECTED to raw_material and changeable. Every line on a
//                    purchase order was bought from a vendor, which is what ROH
//                    means. This is the one place a preselection is honest.
//   commodity_class  preselected ONLY when the line's own words justify it
//                    ("screw" → hardware fasteners; see skuGrammar). Otherwise
//                    empty, and Confirm names the line that is still blank.
//
// The labels and hints come from Parts/classificationDisplay — the same module
// the New Item modal and the catalog filters read, so the words are the same
// words everywhere. The CONTROL differs on purpose: the modal spends ten tiles
// on commodity_class because it has a full drawer, while a line on a scan card
// gets a select, which is one tab stop and one keypress on the way to the next
// line. Same vocabulary, denser surface.
//
// ── TWO WAYS TO CREATE AN ITEM, AND BOTH BELONG HERE ────────────────────────
// The inline card above is the BULK path: a thirty-line scan is reviewed as one
// screen and committed as ONE confirm, where the server mints every new item
// inside the same transaction as the purchase order. Asking a wizard per line
// would turn one press into thirty.
//
// "Create with the wizard…" is the ONE-OFF path, for the line that deserves a
// proper item: a name, a kind, in-place vendor creation, everything the catalog
// page offers. It mints IMMEDIATELY, outside the confirm transaction, and that
// is deliberate: an item is an item whether or not this scan is ever confirmed,
// and the person who just described a part should not lose that work because
// they abandoned the document. The line becomes a MATCHED line the moment the
// wizard answers (part_catalog_id set, mint cleared), which is a state the
// confirm endpoint already accepts — mint_conflicts_with_match fires only when
// a line carries both.

import React, { useState } from 'react';
import type { MaterialType, CommodityClass, PartsCatalogRow } from '../Receiving/types';
import {
  MATERIAL_TYPES,
  MATERIAL_TYPE_META,
  COMMODITY_CLASSES,
  COMMODITY_CLASS_META,
} from '../Parts/classificationDisplay';
import { formatCents } from './poApi';
import { buttonClass } from '../components/ui/Button';
import type { ImportLineType } from './poImportApi';
import ClassificationGuideButton from '../Parts/ClassificationGuide';
// ONE item picker, everywhere an item is looked up. This card used to be handed
// a `catalogSearch` function and run its own typeahead; the picker fetches for
// itself, so both lanes stopped passing one.
import ItemPicker from '../components/ItemPicker';
import { itemLabel, type ItemNameSource } from '../components/ItemName';
// The catalog's own mint form, opened per line. See the header for why this
// lane keeps both it and the inline card.
import NewItemModal from '../Parts/NewItemModal';
// The wizard's step-one question, as a control. One picker over one table:
// a second typeahead here is how two screens learn to disagree about a kind.
import KindPicker from '../Parts/KindPicker';
import SkuBuilder, { emptyBoard, emptyNonBoard } from '../Parts/SkuBuilder';
import {
  DEFAULT_BRAND,
  exampleCode,
  parseBoardSku,
  previewBoardSku,
  suggestCommodityClass,
  suggestTypeToken,
  type Brand,
  type BoardFields,
} from '../Parts/skuGrammar';
import useItemKindsStore, { kindByKey, type ItemKind } from '../stores/useItemKindsStore';
import { laneLabel } from '../components/review/fieldStyles';
import { Alert } from '../components/ui/Alert';
import { Appear } from '../components/ui/Appear';
import Reveal from '../components/ui/Reveal';
import { Checkbox } from '../components/ui/Checkbox';
import { Field, fieldClass } from '../components/ui/Field';
import { Panel } from '../components/ui/Panel';
import { Select } from '../components/ui/Select';
import { Tag } from '../components/ui/Tag';

// py-2 on phone (~40px) and the old density on desktop: every control on this
// card is now something a thumb has to hit while holding a box. That is the
// shared bench field, so it comes from the one place that defines it.
const labelClass = laneLabel;

/** The server's own limit on parts_catalog.name, and the wizard's. */
export const MINT_NAME_MAX = 60;

/**
 * The loose SKU shape is not reachable from this card, so the builder's
 * non-board props are satisfied once at module scope rather than by state
 * modeling a screen nobody can get to. Same device as the wizard's.
 */
const NON_BOARD_UNUSED = emptyNonBoard();
const noop = () => {};

export interface ScanMintDraft {
  /**
   * What sort of thing this is: `item_kinds.key`. NULL means nobody has said
   * yet, which is a real answer for a line the reader could not place, and it
   * blocks Confirm by name rather than guessing a kind onto the catalog.
   */
  kindKey: string | null;
  /**
   * What people will call it. REQUIRED for a short kind, since a short code
   * says nothing about the thing and the name is what every screen renders.
   * A board generates its own from its segments, so this stays empty there.
   */
  name: string;
  /**
   * A BOARD ONLY, and derived rather than typed: `previewBoardSku(board)`.
   * A short kind carries none at all — the server assigns STEM-XXXX at mint.
   */
  sku?: string;
  /**
   * The segments behind `sku`, and the source of truth for a board. Written
   * only through `boardMint`, so the two can never disagree.
   */
  board?: BoardFields;
  materialType: MaterialType | '';
  commodityClass: CommodityClass | '';
  /**
   * ITEM IDENTITY: who makes it, and their number for it. Both OPTIONAL, both
   * rendered on every lane since 2026-08-07.
   *
   * MPN used to be quote-lane-only behind a `showMpn` flag, on the reasoning that
   * "the scan lane does not use it". That was wrong in the ordinary way: a
   * scanned purchase order prints the supplier's part number next to the
   * description exactly like a quote does, and hiding the field meant every SKU
   * that lane minted carried nobody's name, forever. Mannie made it universal:
   * "all of the item minting should attempt to suggest a manufacturer."
   *
   * Read and written as a PAIR, because an MPN without a manufacturer is
   * ambiguous across distributors.
   *
   * Deliberately the reviewer's to clear: a part number is the MANUFACTURER's
   * when the document came from the manufacturer, and the DISTRIBUTOR's order
   * code when it did not. Only the person reading the paper knows which, and a
   * wrong MPN is worse than an empty one because future matching indexes on it.
   */
  mpn?: string;
  manufacturer?: string;
}

export interface ScanLineDraft {
  key: string;
  lineNumber: number;
  description: string;
  /**
   * What the DOCUMENT said, kept whether or not this line is minting.
   *
   * Held here rather than only on the mint card so that switching a matched line
   * to "create a new item" can reuse the read's own suggestions. Without them
   * that switch starts blank, which silently punishes changing your mind about a
   * match — and the person doing it is the one who just noticed the match was
   * wrong, which is exactly when they deserve the help.
   */
  partNumber?: string | null;
  manufacturer?: string | null;
  quantity: number;
  unitCostCents: number | null;
  /** The price AS PRINTED, when it had more precision than a cent. Carried
   *  untouched to confirm so the server can note the rounding on the line —
   *  sub-cent unit prices are 172 of ~440 lines in the real Firefly book. */
  unitPriceText: string | null;
  unitPriceExact: boolean;
  lineType: ImportLineType;
  included: boolean;
  /** Set when this line points at an item that already exists. */
  partCatalogId: number | null;
  matchedSku: string | null;
  /**
   * The matched row's NAME fields, set only when a person pointed this line at
   * an item through the picker. The AI match arrives as a `sku_guess` with no
   * name attached, so a line matched by the READ still shows its code — which is
   * honest: that code is what the reader matched on.
   */
  matchedItem?: ItemNameSource | null;
  /** Set when this line is creating one. Never both. */
  mint: ScanMintDraft | null;
}

/** Which field on this card is blocking Confirm, if any. */
export interface ScanLineError {
  field: 'sku' | 'kind' | 'name' | 'material_type' | 'commodity_class';
  message: string;
}

/**
 * The ONE writer of a board draft's two halves.
 *
 * `board` is what the builder edits and `sku` is what the payload sends, and a
 * component that set one without the other is how a card would show FF-PYR-02-3
 * over a line that mints FF-PYR-02-2. Every transition that touches the
 * segments goes through here: the drafting below, and the builder's onChange.
 */
export function boardMint(board: BoardFields): Pick<ScanMintDraft, 'board' | 'sku'> {
  return { board, sku: previewBoardSku(board) };
}

/** A kind that is still on offer. A retired one stays on the items already
 *  wearing it and is not a choice for a new one. */
function activeKind(kinds: ItemKind[], key: string | null | undefined): ItemKind | null {
  if (!key) return null;
  const found = kindByKey(kinds, key);
  return found && found.is_active ? found : null;
}

export interface MintDraftInput {
  /** The line's own words, off the document. */
  description: string;
  /** The taxonomy, from useItemKindsStore. Empty means nothing can be guessed
   *  and every line asks, which is the honest answer when it has not loaded. */
  kinds: ItemKind[];
  /** Which brand this document mints under. Firefleet's build says FF; the
   *  quote lane reads it off the parse response, which is server truth. */
  brand?: Brand;
  /** The reader's own code proposal for this line, when it made one. */
  skuGuess?: string | null;
  partNumber?: string | null;
  manufacturer?: string | null;
}

/**
 * The mint a scanned or quoted line is born with.
 *
 * THE KIND IS GUESSED, NEVER ASSUMED. Two routes, most-specific first:
 *
 *   1. The reader proposed a BOARD-SHAPED code. Its second segment IS a kind
 *      (`FF-PYR-02-1-CH` is a Pyro), so the string is taken apart into the
 *      segments the builder edits and the line opens on the board branch.
 *   2. The description's own type token — the same word `suggestItemSku` used
 *      to put in the SKU's second segment — is looked up in `item_kinds`.
 *      "M3x8 socket screw" gives SCR; if SCR is a live kind, the line knows
 *      what it is.
 *
 * Nothing matched means `kindKey: null`, and the card asks. A guess that
 * invents a kind is a wrong code minted forever, which is exactly the failure
 * short codes exist to remove.
 */
export function draftMint({
  description,
  kinds,
  brand = DEFAULT_BRAND,
  skuGuess,
  partNumber,
  manufacturer,
}: MintDraftInput): ScanMintDraft {
  const shared = {
    // The SAP-honest answer for anything bought on a purchase order or quoted
    // by a supplier, and the only preselection on this card defensible from the
    // fact of being on the document at all.
    materialType: 'raw_material' as MaterialType,
    // Preselected ONLY when the line's own words justify it. Otherwise empty,
    // and Confirm names the line: a wrong preselection is a wrong answer
    // wearing the look of a reviewed one.
    commodityClass: suggestCommodityClass(description),
    mpn: partNumber || '',
    manufacturer: manufacturer || '',
  };

  const parsed = parseBoardSku(skuGuess);
  const parsedKind = parsed ? activeKind(kinds, parsed.board) : null;
  if (parsed && parsedKind?.code_mode === 'structured') {
    return { kindKey: parsedKind.key, name: '', ...boardMint(parsed), ...shared };
  }

  const kind = activeKind(kinds, suggestTypeToken(description));
  if (kind?.code_mode === 'structured') {
    return {
      kindKey: kind.key,
      name: '',
      ...boardMint({ ...emptyBoard(), brand, board: kind.key }),
      ...shared,
    };
  }

  return {
    // A short kind, or nothing recognized. Either way the document's own
    // wording is the best name anybody has, and it is what the person reading
    // the box would type back in.
    kindKey: kind ? kind.key : null,
    name: description.trim().slice(0, MINT_NAME_MAX),
    ...shared,
  };
}

const LINE_TYPE_LABELS: Record<ImportLineType, string> = {
  stock: 'Goods',
  service: 'Service',
  charge: 'Charge',
};

export const PoScanLineCard: React.FC<{
  line: ScanLineDraft;
  error: ScanLineError | null;
  /**
   * The review kit's amber ring for this line while its slot is open: a class
   * string carrying its own leading space (`ReviewSlot.ring`), appended to the
   * card's border classes so the amber lands after the gray it overrides.
   *
   * The quote lane passes `slot?.ring`, ringing the whole card the way the
   * catalog-import and packing-list lanes ring theirs: on those lanes the doubt
   * is about a ROW rather than a field in it. The scan lane passes nothing, so
   * its cards render unringed: there the flagged INPUTS wear the amber, which
   * is that lane's own choice about where the doubt sits.
   *
   * Deliberately narrow rather than a general `className`: this card is shared
   * by two lanes, and an open styling seam on a shared card is how two screens
   * drift apart one utility at a time.
   */
  flagClass?: string;
  onPatch: (patch: Partial<ScanLineDraft>) => void;
  onPatchMint: (patch: Partial<ScanMintDraft>) => void;
  /** Swap between "this is a new item" and "point at one we have". */
  onSwitchToMint: () => void;
  onSwitchToMatch: () => void;
  /**
   * Vendors with a two-letter code, for the manufacturer segment of a board
   * code. Fetched ONCE by the page (`GET /procurement/vendors`, filtered to the
   * ones with a code) and handed down, because a document is thirty of these
   * cards and thirty fetches of one list is thirty fetches of one list.
   */
  codedVendors?: { id: string; code: string | null; name: string }[];
  /**
   * Offer "New vendor…" as the last option of the manufacturer segment. The
   * page owns the form it opens (one drawer per page, not one per card), and
   * hands the created vendor back into this card's segment through onPatchMint.
   */
  onNewVendor?: () => void;
  /** Which brand a board composed on this card mints under. Defaults to the
   *  build's own; the quote lane overrides it from the parse response. */
  brand?: Brand;
}> = ({
  line,
  error,
  flagClass,
  onPatch,
  onPatchMint,
  onSwitchToMint,
  onSwitchToMatch,
  codedVendors = [],
  onNewVendor,
  brand = DEFAULT_BRAND,
}) => {
  // Read, not loaded: KindPicker below owns the fetch, and this only needs to
  // know which branch the chosen kind implies.
  const kinds = useItemKindsStore((s) => s.kinds);
  const mintKind = activeKind(kinds, line.mint?.kindKey);
  const isBoardMint = mintKind?.code_mode === 'structured';

  /**
   * Answering "what is it" reshapes the rest of the card, so the switch clears
   * whatever belonged to the other branch rather than leaving it to be sent.
   *
   * A board keeps its segments across a change of board kind (a re-kind is
   * usually a correction, and the revision and assembler are still true), and a
   * short kind arrives named from the document, which is where the name would
   * have come from anyway.
   */
  const changeKind = (chosen: ItemKind) => {
    if (chosen.key === line.mint?.kindKey) return;
    if (chosen.code_mode === 'structured') {
      onPatchMint({
        kindKey: chosen.key,
        name: '',
        ...boardMint({ ...(line.mint?.board || { ...emptyBoard(), brand }), board: chosen.key }),
      });
      return;
    }
    onPatchMint({
      kindKey: chosen.key,
      name: line.mint?.name || line.description.trim().slice(0, MINT_NAME_MAX),
      sku: undefined,
      board: undefined,
    });
  };
  // Name if a person picked it, code if the read matched it. Never both: two
  // identifiers on one chip is how a person stops reading either.
  const matchedLabel = line.matchedItem ? itemLabel(line.matchedItem) : line.matchedSku;

  /** The wizard, or null while it is shut. The string is what the reviewer had
   *  typed into the picker before giving up on it; empty when the wizard was
   *  opened from the mint card, where the document has already said its piece.
   *  The card owns this rather than the page: the modal belongs to ONE line,
   *  and lifting it would mean threading a line key back down again. */
  const [wizardSeed, setWizardSeed] = useState<string | null>(null);

  /**
   * What the wizard opens with in its name field.
   *
   * DOCUMENT FIDELITY WINS, the same rule the inline mint follows: the vendor's
   * own wording for the line is the best name anybody has, and the typed seed is
   * the fallback for a line the paper named nothing.
   *
   * Skipped entirely when the suggested SKU is board-shaped. A board's name is
   * generated from its segments and the wizard previews it live, so seeding the
   * field would put a vendor's description where "Gen2 Pyro (V24, rev 1)"
   * belongs.
   */
  const wizardName = (): string | undefined => {
    if (line.mint?.board) return undefined;
    return line.description.trim() || wizardSeed?.trim() || undefined;
  };

  // The wizard answers with the whole catalog row, so this line stops being a
  // mint and becomes a match. Both cannot be set: the confirm endpoint refuses
  // that pair (mint_conflicts_with_match).
  const takeMintedItem = (row: PartsCatalogRow) => {
    onPatch({ partCatalogId: row.id, matchedSku: row.sku, matchedItem: row, mint: null });
    setWizardSeed(null);
  };

  /**
   * The reviewer overruling the parse, which no classifier gets right every time:
   * "PCBA" is a part on one purchase order and labor on the next, and only the
   * person holding the box knows which. The read is a suggestion; this is the
   * answer. (The import lane has had this control since it shipped; the scan and
   * quote lanes rendered the same field as a label, so a good read as a service
   * could not be received at all — the item picker below is hidden on a non-stock
   * line, so there was no way to give it a SKU.)
   *
   * Leaving 'stock' also clears the item, and that is not tidiness. po-scan-service
   * REJECTS a mint on a non-stock line (`mint_on_non_stock_line`), so carrying one
   * over would fail at Confirm with an error naming a field the card is no longer
   * showing; and a matched SKU on a freight line is a claim nobody meant to make.
   * Clearing on change rather than at confirm makes the card show the consequence
   * at the moment the choice is made.
   */
  const changeType = (next: ImportLineType) => {
    if (next === line.lineType) return;
    onPatch(
      next === 'stock'
        ? { lineType: next }
        : { lineType: next, partCatalogId: null, matchedSku: null, matchedItem: null, mint: null }
    );
  };

  const isCharge = line.lineType !== 'stock';

  return (
    <div
      // A card in a list, so it says so by tone: one step above the page it
      // sits on, and no outline. An excluded line drops back to the page's own
      // tone and dims, which reads as "set aside" rather than "grayed out box".
      // Trouble is a RING, not a border — the card has none to recolor, and a
      // ring occupies no layout so a flagged card does not shift its neighbors.
      className={`rounded-lg px-3 py-2.5 transition-colors duration-150 ease-emphasized motion-reduce:transition-none ${
        line.included ? 'bg-surface-container-low' : 'bg-surface opacity-60'
      } ${error ? 'ring-2 ring-red-500/60' : ''}${flagClass || ''}`}
      data-testid={`scan-line-${line.lineNumber}`}
    >
      <div className="flex items-start gap-3">
        <Checkbox
          checked={line.included}
          onChange={(e) => onPatch({ included: e.target.checked })}
          aria-label={`Include line ${line.lineNumber}`}
          className="mt-1 shrink-0"
        />
        <div className="flex-1 min-w-0 space-y-2">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-xs text-on-surface-variant">{line.lineNumber}</span>
            <Field
              className="flex-1 min-w-[8rem]"
              value={line.description}
              onChange={(e) => onPatch({ description: e.target.value })}
              aria-label={`Description, line ${line.lineNumber}`}
            />
            <Select
              label={`Line type, line ${line.lineNumber}`}
              size="sm"
              width="fit"
              className="shrink-0 self-center"
              value={line.lineType}
              onChange={(key) => changeType(key as ImportLineType)}
              options={(['stock', 'service', 'charge'] as ImportLineType[]).map((t) => ({ key: t, label: LINE_TYPE_LABELS[t] }))}
            />
          </div>

          <div className="flex items-end gap-2 flex-wrap">
            <label className="block w-24">
              <span className={labelClass}>Quantity</span>
              <Field
                type="number"
                min={1}
                value={line.quantity}
                onChange={(e) => onPatch({ quantity: parseInt(e.target.value, 10) || 0 })}
                aria-label={`Quantity, line ${line.lineNumber}`}
              />
            </label>
            <div className="pb-2 text-xs text-on-surface-variant">
              at {line.unitCostCents === null ? 'no price' : formatCents(line.unitCostCents)} each
              <Appear show={line.unitCostCents !== null && line.quantity > 0} className="text-on-surface-variant">
                {' · '}{line.unitCostCents !== null ? formatCents(line.unitCostCents * line.quantity) : ''}
              </Appear>
            </div>
          </div>

          {/* ── the item ─────────────────────────────────────────────────── */}
          {isCharge ? (
            <p className="text-xs text-on-surface-variant">
              Freight and services do not go on a shelf, so this line needs no item.
            </p>
          ) : line.mint ? (
            // One step ABOVE the card: the sub-form is a surface sitting on it,
            // not a boxed-off region of it. The primary heading below is what
            // says "this line is minting something new"; a tinted wash over the
            // whole panel said it a second time and fought the card's own tone.
            <Panel on="panel" padding="sm" label="New item" className="space-y-2.5">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="text-xs font-medium text-primary">New item</span>
                <div className="flex items-center gap-3">
                  {/* Right where the two selects below are about to ask. Link
                      text rather than a "?" circle: a second unlabeled glyph
                      inside a card reads as decoration and gets ignored. */}
                  <ClassificationGuideButton />
                  {/* The way out of the quick card, for the line that wants a
                      real item: a name, a kind, a manufacturer created in
                      place. See the file header on why both paths exist. */}
                  <button
                    type="button"
                    onClick={() => setWizardSeed('')}
                    className={buttonClass('quiet', 'sm')}
                  >
                    Create with the wizard…
                  </button>
                  <button
                    type="button"
                    onClick={onSwitchToMatch}
                    className={buttonClass('quiet', 'sm')}
                  >
                    Use an item we already have
                  </button>
                </div>
              </div>

              {/* ── WHAT SORT OF THING IS IT ─────────────────────────────────
                  First, because the answer decides what the rest of this card
                  asks. Same control and same list as step one of the wizard. */}
              <div>
                <KindPicker
                  size="sm"
                  id={`scan-kind-${line.lineNumber}`}
                  label="Kind"
                  value={line.mint.kindKey}
                  onChange={changeKind}
                  partNumberHint={line.mint?.mpn || line.partNumber || null}
                  inputClassName={fieldClass({ error: error?.field === 'kind' })}
                  labelClassName={labelClass}
                />
                <Reveal show={!line.mint.kindKey}>
                  <p className="mt-1 text-[10px] text-on-surface-variant">
                    What sort of thing this is. It decides how the code is made, so nothing
                    else on this card can be answered first.
                  </p>
                </Reveal>
              </div>

              {/* ── THE CODE, OR THE ABSENCE OF ONE ──────────────────────────
                  A board composes its SKU segment by segment, because every
                  segment is a decision somebody made and the server parses them
                  back into a part family. Everything else takes a generated
                  short code and says nothing about it here: there is no field
                  to fill in, which is the whole point of short codes. */}
              {isBoardMint && line.mint.board ? (
                // The vendor form does not live on this card (a card in a list
                // of thirty is nowhere to put one); the page mounts one drawer
                // and this card only asks for it.
                <SkuBuilder
                  shape="board"
                  onShapeChange={noop}
                  board={line.mint.board}
                  onBoardChange={(next) => onPatchMint(boardMint(next))}
                  nonBoard={NON_BOARD_UNUSED}
                  onNonBoardChange={noop}
                  codedVendors={codedVendors}
                  labelClass={labelClass}
                  lockBoard
                  onNewVendor={onNewVendor}
                />
              ) : line.mint.kindKey ? (
                // Keyed on the ANSWER, not on the resolved row: a kind retired
                // in another tab mid-review would otherwise leave a card that
                // asks for nothing while the gate still lets the line through.
                <label className="block">
                  <span className={labelClass}>Name</span>
                  <Field
                    error={error?.field === 'name'}
                    value={line.mint.name}
                    maxLength={MINT_NAME_MAX}
                    onChange={(e) => onPatchMint({ name: e.target.value.slice(0, MINT_NAME_MAX) })}
                    aria-label={`Name, line ${line.lineNumber}`}
                    placeholder="What people call it"
                  />
                  <p className="mt-1 text-[10px] text-on-surface-variant">
                    {mintKind ? (
                      <>
                        Code assigned on confirm, like{' '}
                        <span className="font-mono text-on-surface-variant">
                          {exampleCode(mintKind.key, 'short')}
                        </span>
                        . The name is what every screen shows.
                      </>
                    ) : (
                      'The name is what every screen shows.'
                    )}
                  </p>
                </label>
              ) : null}

              {/* ── WHO MAKES IT, AND THEIR NUMBER FOR IT ────────────────────
                  Side by side because they are one fact in two halves: an MPN
                  with no manufacturer is ambiguous across distributors, and
                  "0603-10K" is a hundred different resistors until somebody says
                  Vishay.

                  This used to be MPN alone, behind a `showMpn` the quote lane
                  passed and the scan lane did not — so every SKU a scanned
                  purchase order minted had nobody's name on it, forever. Mannie,
                  2026-08-07: "all of the item minting should attempt to suggest a
                  manufacturer. we need this going forward." The flag is gone
                  rather than doubled: both lanes read both fields now, and a
                  document that printed neither leaves both blank. */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="block">
                  <span className={labelClass}>Manufacturer part number</span>
                  <Field
                    className="font-mono"
                    value={line.mint.mpn || ''}
                    onChange={(e) => onPatchMint({ mpn: e.target.value })}
                    aria-label={`Manufacturer part number, line ${line.lineNumber}`}
                  />
                  <p className="mt-1 text-[10px] text-on-surface-variant">
                    Future documents match on this, so clear it if that number is the
                    distributor's rather than the manufacturer's.
                  </p>
                </label>

                <label className="block">
                  <span className={labelClass}>Manufacturer</span>
                  <Field
                    value={line.mint.manufacturer || ''}
                    onChange={(e) => onPatchMint({ manufacturer: e.target.value })}
                    aria-label={`Manufacturer, line ${line.lineNumber}`}
                    placeholder="Who makes it"
                  />
                  <p className="mt-1 text-[10px] text-on-surface-variant">
                    Who <span className="text-on-surface">makes</span> it, not who sells it.
                    Mouser, Digi-Key and Arrow are distributors.
                  </p>
                </label>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <label className="block">
                  <span className={labelClass}>Build stage</span>
                  <Select
                    label={`Build stage, line ${line.lineNumber}`}
                    error={error?.field === 'material_type'}
                    placeholder="Select…"
                    value={line.mint.materialType || null}
                    onChange={(key) => onPatchMint({ materialType: key as MaterialType | '' })}
                    options={[{ key: '', label: 'Select…' }, ...MATERIAL_TYPES.map((t) => ({ key: t, label: MATERIAL_TYPE_META[t].label }))]}
                  />
                  <p className="mt-1 text-[10px] text-on-surface-variant">
                    {line.mint.materialType
                      ? MATERIAL_TYPE_META[line.mint.materialType].hint
                      : 'Where this sits in what we make.'}
                  </p>
                </label>

                <label className="block">
                  <span className={labelClass}>Item type</span>
                  <Select
                    label={`Item type, line ${line.lineNumber}`}
                    error={error?.field === 'commodity_class'}
                    placeholder="Select…"
                    value={line.mint.commodityClass || null}
                    onChange={(key) => onPatchMint({ commodityClass: key as CommodityClass | '' })}
                    options={[{ key: '', label: 'Select…' }, ...COMMODITY_CLASSES.map((t) => ({ key: t, label: COMMODITY_CLASS_META[t].label }))]}
                  />
                  <p className="mt-1 text-[10px] text-on-surface-variant">
                    {line.mint.commodityClass
                      ? COMMODITY_CLASS_META[line.mint.commodityClass].hint
                      : 'What kind of thing this is. Nothing guesses it for you.'}
                  </p>
                </label>
              </div>
            </Panel>
          ) : line.partCatalogId ? (
            <div className="flex items-center gap-2 flex-wrap text-sm">
              <Tag tone="good" mono={!line.matchedItem}>
                {matchedLabel}
              </Tag>
              <span className="text-xs text-on-surface-variant">already in the catalog</span>
              <button
                type="button"
                onClick={() => onPatch({ partCatalogId: null, matchedSku: null, matchedItem: null })}
                className={buttonClass('quiet', 'sm')}
              >
                Pick a different item
              </button>
            </div>
          ) : (
            <div className="flex items-end gap-2 flex-wrap">
              <div className="flex-1 min-w-[9rem]">
                <ItemPicker
                  id={`scan-item-${line.lineNumber}`}
                  label="Item"
                  value={null}
                  // Retired items included: the reader's own index matches them
                  // (a scanned PO can name a part we stopped buying), so the hand
                  // path must reach the same row rather than mint a twin.
                  extraParams={{ include_inactive: 'true' }}
                  onSelect={(row) => {
                    if (!row) return;
                    onPatch({
                      partCatalogId: row.id,
                      matchedSku: row.sku,
                      matchedItem: row,
                      mint: null,
                    });
                  }}
                  // Searched, found nothing: that person has already described
                  // the part in their own words, so they get the full wizard
                  // rather than a four-field card.
                  onCreate={(typed) => setWizardSeed(typed)}
                  placeholder="Type a name, SKU or part number"
                  inputClassName={fieldClass({ error: !!error })}
                />
              </div>
              {/* The picker offers "New item…" inside its list, which is where
                  somebody who searched and found nothing looks. This button is
                  for the other person: the one who already knows the line is new
                  and never wanted to search at all, and who wants the quick card
                  rather than the wizard. */}
              {/* Wrapped, not passed bare: onClick would hand the handler a
                  MouseEvent as its first argument. */}
              <button
                type="button"
                onClick={() => onSwitchToMint()}
                className={`${buttonClass('quiet', 'sm')} shrink-0`}
              >
                Create a new item
              </button>
            </div>
          )}

          <Alert show={!!error} tone="error" variant="text" live="alert" className="!text-xs !leading-4">
            {error?.message}
          </Alert>
        </div>
      </div>

      {wizardSeed !== null && (
        <NewItemModal
          initialName={wizardName()}
          // The card already asked what sort of thing this is, so the wizard
          // opens on step two rather than re-asking. A preselect, not a lock:
          // its step rail is one press back.
          initialKindKey={line.mint?.kindKey || undefined}
          hintText={line.description || undefined}
          onMinted={takeMintedItem}
          onClose={() => setWizardSeed(null)}
        />
      )}
    </div>
  );
};

export default PoScanLineCard;
